import { personApi } from '@/store/api/person.api.ts';
import { rootApi } from '@/store/api/root.api.ts';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { alertSlice } from './alerts/slice';
import { agentRootApi } from './api/agentRoot.api';
import { alertApi } from './api/alert.api';
import { cashTransferApi } from './api/cashTransfer.api';
import { documentApi } from './api/document.api';
import { instrumentApi } from './api/instrument.api';
import { userPreferencesApi } from './api/userPreferences.api';
import { authorisationsSlice } from './authorisations/slice';
import { cashTransferFilterSlice } from './cashTransfer/filterSlice';
import { cashTransferSlice } from './cashTransfer/slice';
import { cashTransferModalSlice } from './global/cashTransferModalSlice';
import { dateSlice } from './global/dataSlice';
import { childFetchingSlice } from './global/detailedValuationSlice';
import { generateIbanSlice } from './global/generateIbanSlice';
import { generateOperationSlice } from './global/generateOperationModalSlice';
import { generatePerformanceSlice } from './global/generatePerformanceSlice';
import { generatePortfolioSlice } from './global/generatePortfolioSlice';
import { openedAccountPagesSlice } from './global/openedAccountPages';
import { rootDetailsTabsSlice } from './global/rootDetailsTabsSlice';
import { instrumentSlice } from './instruments/slice';
import { operationSlice } from './operations/slice';
import { personsSlice } from './persons/slice';
import { documentToastSlice } from './toasts/documentToastSlice';
import { globalToastsSlice } from './toasts/globalToastsSlice';
import { userPreferencesSlice } from './user/userPreferencesSlice';
import { userSlice } from './user/userSlice';
import { warrantiesSlice } from './warranties/slice';
import { auditApi } from './api/auditApi.api';
import { positionSlice } from './position/slice';

const rootReducer = combineReducers({
  // API
  [rootApi.reducerPath]: rootApi.reducer,
  [personApi.reducerPath]: personApi.reducer,
  [alertApi.reducerPath]: alertApi.reducer,
  [agentRootApi.reducerPath]: agentRootApi.reducer,
  [instrumentApi.reducerPath]: instrumentApi.reducer,
  [cashTransferApi.reducerPath]: cashTransferApi.reducer,
  [auditApi.reducerPath]: auditApi.reducer,
  [documentApi.reducerPath]: documentApi.reducer,
  [userPreferencesApi.reducerPath]: userPreferencesApi.reducer,

  // Slice
  [rootDetailsTabsSlice.reducerPath]: rootDetailsTabsSlice.reducer,
  [alertSlice.reducerPath]: alertSlice.reducer,
  [operationSlice.reducerPath]: operationSlice.reducer,
  [cashTransferSlice.reducerPath]: cashTransferSlice.reducer,
  [instrumentSlice.reducerPath]: instrumentSlice.reducer,
  [openedAccountPagesSlice.reducerPath]: openedAccountPagesSlice.reducer,
  [cashTransferModalSlice.reducerPath]: cashTransferModalSlice.reducer,
  [userSlice.reducerPath]: userSlice.reducer,
  [generateIbanSlice.reducerPath]: generateIbanSlice.reducer,
  [dateSlice.reducerPath]: dateSlice.reducer,
  [childFetchingSlice.reducerPath]: childFetchingSlice.reducer,
  [generatePortfolioSlice.reducerPath]: generatePortfolioSlice.reducer,
  [generatePerformanceSlice.reducerPath]: generatePerformanceSlice.reducer,
  [documentToastSlice.reducerPath]: documentToastSlice.reducer,
  [globalToastsSlice.reducerPath]: globalToastsSlice.reducer,
  [authorisationsSlice.reducerPath]: authorisationsSlice.reducer,
  [cashTransferFilterSlice.reducerPath]: cashTransferFilterSlice.reducer,
  [warrantiesSlice.reducerPath]: warrantiesSlice.reducer,
  [generateOperationSlice.reducerPath]: generateOperationSlice.reducer,
  [userPreferencesSlice.reducerPath]: userPreferencesSlice.reducer,
  [personsSlice.reducerPath]: personsSlice.reducer,
  [positionSlice.reducerPath]: positionSlice.reducer,
});

export function setupStore(preloadedState?: Partial<AppState>) {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        rootApi.middleware,
        personApi.middleware,
        userPreferencesApi.middleware,
        alertApi.middleware,
        agentRootApi.middleware,
        instrumentApi.middleware,
        cashTransferApi.middleware,
        documentApi.middleware,
        auditApi.middleware,
      ),
  });
}

export type AppState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore['dispatch'];

export const store = setupStore();
